import { getCurrentInstance, watch, onBeforeUnmount } from 'vue'
import { storeToRefs } from 'pinia'
import { useMainStore } from '@/store/main'
import { useLocationStore } from '@/store/location'
import { useStatusStore } from '@/store/status'
import { useUserFlagsStore } from '@/store/userFlags'
import { useSubscriptionStore } from '@/store/subscription'
import absmartly from '@absmartly/javascript-sdk'
import stringHash from '@sindresorhus/string-hash'
import Sentry from '@/modules/Sentry'
import lscache from 'lscache'
import uuid from 'uuid-random'
import { parseISO, getUnixTime } from 'date-fns'

import config from '@/config'
import Jet from '@/modules/Jet'
import { os } from '@/helpers/device.js'
import { ENV_DEV_OR_STAGING, ENV_HACKERONE, VUE_APP_VERSION } from '@/helpers/environment'
import { DOMAINS_FROM_CONFIG } from '@/funnels/funnels.ts'

const absmartlySDK = new absmartly.SDK({
	endpoint: `${config('AbSmartlyApiHost')}/v1`,
	apiKey: config('AbSmartlyApiKey'),
	environment: ENV_DEV_OR_STAGING || ENV_HACKERONE ? 'Development' : 'Production',
	application: 'Web',
	timeout: 7000,
})

const trackedExperiments = {}
const getVariantLetterByNumber = (number) => String.fromCharCode(65 + number)

/**
 * Generates an uuid and caches it in `localStorage` forever
 * It is necessary for using unit `deviceId` in the A/B Smartly
 */
export function setDeviceId() {
	let deviceId = lscache.get('deviceId')

	if (!deviceId) {
		deviceId = uuid()
		lscache.set('deviceId', deviceId)
	}

	window.deviceId = deviceId
}

const calcBucket = function (id) {
	if (typeof id !== 'string') {
		return -1
	}

	const hash = stringHash(id)

	return hash % 10
}

const getOverrideObjectFromUrlParams = function (queryParamsExp) {
	if (typeof queryParamsExp === 'string') {
		queryParamsExp = [queryParamsExp]
	}

	return queryParamsExp.reduce((result, queryParamsString) => {
		const [name, variant] = queryParamsString.split('~')
		const variantNumber = parseInt(variant)

		if (isNaN(variantNumber)) {
			return result
		}

		return { ...result, [name]: variantNumber }
	}, {})
}

export function useExperiments() {
	const appInstance = getCurrentInstance()
	const proxy = appInstance.proxy

	const mainStore = useMainStore()
	const locationStore = useLocationStore()
	const statusStore = useStatusStore()
	const userFlagsStore = useUserFlagsStore()
	const subscriptionStore = useSubscriptionStore()

	const {
		user,
		getAbSmartlyExposures: abSmartlyExposures,
		isOnboarded,
		getCountryCode: countryCode,
		getRegionCode: regionCode,
	} = storeToRefs(mainStore)
	const {
		getAppAreaConfig: appAreaConfig,
		getQueryParam: getQueryParamRef,
		hostname,
		isCrmAutologin,
	} = storeToRefs(locationStore)
	const { networkResourceIsReady } = storeToRefs(statusStore)
	const { getIsOrganicTrafficOnboarding: isOrganicTrafficOnboarding } = storeToRefs(userFlagsStore)
	const { checkPeriodType: subscriptionPeriodType, hasSubscription } = storeToRefs(subscriptionStore)

	const { addAbSmartlyExposure, removeAbSmartlyExposure, sendAbSmartlyExposuresToBraze } = mainStore

	// getQueryParam is a getter that returns a function — we need to call .value to get the fn
	const getQueryParam = (key) => getQueryParamRef.value(key)

	let abSmartlyContext = null

	const isExperimentsDisabled = () => {
		const isExperimentsDisabledByQueryParam = Boolean(getQueryParam('safe'))
		const isExperimentsEnabledByQueryParam = Boolean(getQueryParam('unsafe'))
		const utmCampaign = getQueryParam('utm_campaign')
		const isProductionNonExpDomain = !DOMAINS_FROM_CONFIG.find((item) => item.regex.test(hostname.value))?.domainConfig
			?.experiments

		if (isExperimentsEnabledByQueryParam) {
			return false
		}

		return (
			isExperimentsDisabledByQueryParam ||
			isOrganicTrafficOnboarding.value ||
			isProductionNonExpDomain ||
			utmCampaign?.toString().includes('Discovery') ||
			isCrmAutologin.value
		)
	}

	function setAbSmartlyAttributes() {
		const utmSource = getQueryParam('utm_source')
		const utmCampaign = getQueryParam('utm_campaign')
		const utmGender = getQueryParam('utm_gender')
		const utmTerm = getQueryParam('utm_term')
		const utmContent = getQueryParam('utm_content')
		const hostnameVal = ENV_DEV_OR_STAGING ? getQueryParam('host') ?? window.location.hostname : hostname.value

		const attributes = {
			device: os,
			device_bucket: calcBucket(window.deviceId),
			deviceId: window.deviceId,
			user_language: window.language,
			app_version: VUE_APP_VERSION,
			hostname: hostnameVal,
			app_area: appAreaConfig.value.appArea,
		}

		if (utmSource && typeof utmSource === 'string') {
			attributes.utm_source = utmSource.toLowerCase()
		}

		if (utmCampaign && typeof utmCampaign === 'string') {
			attributes.utm_campaign = utmCampaign.toLowerCase()
		}

		if (utmTerm && typeof utmTerm === 'string') {
			attributes.utm_term = utmTerm.toLowerCase()
		}

		if (utmContent && typeof utmContent === 'string') {
			attributes.utm_content = utmContent.toLowerCase()
		}

		if (utmGender) {
			attributes.utm_gender = utmGender
		}

		abSmartlyContext.attributes(attributes)

		setAbSmartlyUserAttributes(user.value)
		setAbSmartlySubscriptionAttributes()
		setAbSmartlyCountryAttributes(countryCode.value, regionCode.value)
	}

	function setAbSmartlyUserAttributes(userVal) {
		if (!userVal) {
			return
		}

		abSmartlyContext.attributes({
			user_bucket: calcBucket(userVal.id),
			user_created_timestamp: getUnixTime(parseISO(userVal.createdAt)),
		})
	}

	function setAbSmartlySubscriptionAttributes() {
		if (!hasSubscription.value) {
			return
		}

		abSmartlyContext.attributes({
			has_subscription: subscriptionPeriodType.value.isRegular,
			has_trial: subscriptionPeriodType.value.isCommitment || subscriptionPeriodType.value.isTrial,
		})
	}

	function setAbSmartlyCountryAttributes(countryCodeVal, regionCodeVal) {
		if (!countryCodeVal) {
			return
		}

		abSmartlyContext.attributes({
			country: countryCodeVal.toUpperCase(),
			region_code: regionCodeVal?.toUpperCase?.(),
		})
	}

	function setAbSmartlyAnalyticsUserProperty() {
		const exposuresList = abSmartlyExposures.value.reduce((list, exposure) => {
			const { name, iteration, variantLetter } = exposure
			const underscoredName = name.replaceAll(' ', '_')

			list.push(`${underscoredName}(${variantLetter})(${`i${iteration}`})`)

			return list
		}, [])

		proxy.$analytic.setUserProperties({ active_test_groups: exposuresList })
	}

	function overrideAbSmartlyTreatmentVariants() {
		const queryParamsExp = getQueryParam('exp')

		if (isExperimentsDisabled()) {
			const experiments = getAbSmartlyContextExperiments()

			if (!Array.isArray(experiments)) {
				return
			}

			const overrideObject = experiments.reduce((result, experimentId) => ({ ...result, [experimentId]: 0 }), {})

			if ('web_glp_upsell_fakedoor' in overrideObject) {
				delete overrideObject.web_glp_upsell_fakedoor
			}

			abSmartlyContext.overrides(overrideObject)
		}

		if (queryParamsExp) {
			const overrideObject = getOverrideObjectFromUrlParams(queryParamsExp)

			abSmartlyContext.overrides(overrideObject)

			for (const exp in overrideObject) {
				abSmartlyExposureHandler({
					assigned: true,
					eligible: true,
					fullOn: false,
					name: exp,
					variant: overrideObject[exp],
				})
			}
		}
	}

	function trackExperimentEnd() {
		const { experiments = [] } = abSmartlyContext.data()
		const exposures = abSmartlyExposures.value

		for (const exposure of exposures) {
			const experimentData = experiments.find((experiment) => {
				return experiment.name === exposure.name && experiment.iteration === exposure.iteration
			})

			if (!experimentData) {
				proxy.$analytic.logEvent('Experiment - End', {
					Name: exposure.name,
					Iteration: `i${exposure.iteration}`,
					Variant: exposure.variantLetter,
					VariantName: exposure.variantName,
				})

				removeAbSmartlyExposure({ name: exposure.name, iteration: exposure.iteration })
			}
		}
	}

	function sendAbSmartlyExposureToAnalyticsAndBraze(exposure) {
		const { name, iteration, variantLetter, variantName } = exposure

		proxy.$analytic.logEvent('Experiment - Exposure', {
			Name: name,
			Iteration: `i${iteration}`,
			Variant: variantLetter,
			VariantName: variantName,
		})

		Jet.logLogicalEvent('ExperimentExposure', {
			name,
			iteration: `i${iteration}`,
			variant: variantLetter,
		})

		if (isOnboarded.value) {
			sendAbSmartlyExposuresToBraze({
				[`${name}_${iteration}`]: exposure,
			})
		}
	}

	function getAbSmartlyContextExperiments() {
		try {
			return abSmartlyContext.experiments()
		} catch (e) {
			return []
		}
	}

	function abSmartlyReady() {
		const globalProperties = appInstance?.appContext?.config?.globalProperties || {}
		globalProperties.$absmartly = abSmartlyContext
		mainStore.setAbSmartlyReady()
		globalProperties.$absmartly.assign = (experimentId) => {
			const queryParamsExp = getQueryParam('exp')
			const overrideObject = queryParamsExp ? getOverrideObjectFromUrlParams(queryParamsExp) : {}
			const isExperimentOverridden = Boolean(experimentId in overrideObject)

			const assignment = abSmartlyContext._assign(experimentId)
			if (!assignment.assigned && isExperimentOverridden) {
				return {
					variant: overrideObject[experimentId],
					assigned: true,
				}
			}

			return {
				variant: assignment.variant,
				assigned: assignment.assigned,
			}
		}

		// Proxy for the `treatment` method to return 0 if the experiment is not assigned
		globalProperties.$absmartly.treatment = new Proxy(abSmartlyContext.treatment, {
			apply(method, thisObject, argsList) {
				const experimentName = argsList[0]
				const assignment = thisObject._assign(experimentName)

				if (!assignment.assigned) {
					if (!assignment.overridden) {
						return 0
					} else {
						if (ENV_DEV_OR_STAGING && !(experimentName in trackedExperiments)) {
							trackedExperiments[experimentName] = assignment.variant
							/* eslint-disable no-console */
							console.groupCollapsed(
								`%cA/B Smartly [debug]: %cexposure — ${experimentName} — ${getVariantLetterByNumber(
									assignment.variant,
								)}`,
								'font-weight: 400',
								assignment.variant ? 'color: #669933' : 'font-weight: 400',
							)
							console.log({
								experimentName,
								variant: getVariantLetterByNumber(assignment.variant),
								assignment,
							})
							console.groupEnd()
							/* eslint-enable no-console */
						}
						return assignment.variant
					}
				}

				return method.apply(thisObject, argsList)
			},
		})

		overrideAbSmartlyTreatmentVariants()

		if (!getQueryParam('exp')) {
			trackExperimentEnd()
		}

		useStatusStore().setResourceReadiness('absmartly')
	}

	function abSmartlyExposureHandler(data) {
		const { assigned, eligible, fullOn, name, variant } = data

		if (!assigned || !eligible || fullOn) {
			return
		}

		const { experiments } = abSmartlyContext.data()
		const variantLetter = getVariantLetterByNumber(variant)
		const experimentData = experiments.find((experiment) => experiment.name === name)

		const variantName = '' // legacy
		const iteration = experimentData?.iteration || 0

		addAbSmartlyExposure({ name, iteration, variantLetter, variantName })
	}

	function abSmartlyError(error) {
		const abSmartlyWarning = ['TimeoutError', 'RetryError'].includes(error.name)

		if (abSmartlyWarning) {
			return
		}

		Sentry.withScope((scope) => {
			scope.setExtras({
				attributes: abSmartlyContext?.getAttributes(),
				units: abSmartlyContext?.getUnits(),
				experiments: getAbSmartlyContextExperiments(),
				exposuresLocalStorage: abSmartlyExposures.value,
			})
			scope.setTags({
				abSmartlyError: 'error',
			})
			scope.setTransactionName('A/B Smartly error')
			scope.captureException(error)
		})
	}

	function abSmartlyEventLogger(context, eventName, data) {
		if (eventName === 'error') {
			abSmartlyError(data)
		}

		if (eventName === 'exposure') {
			abSmartlyExposureHandler(data)
		}

		if (ENV_DEV_OR_STAGING) {
			/* eslint-disable no-console */
			if (eventName === 'exposure') {
				console.groupCollapsed(
					`%cA/B Smartly [debug]: %cexposure — ${data.name} — ${getVariantLetterByNumber(data.variant)}`,
					'font-weight: 400',
					eventName === 'exposure' ? 'color: #669933' : 'font-weight: 400',
				)
			} else {
				console.groupCollapsed(`%cA/B Smartly [debug]: ${eventName}`, 'font-weight: 400')
			}
			console.log(context)
			console.log(data)
			console.groupEnd()
			/* eslint-enable no-console */
		}
	}

	function initAbSmartly() {
		absmartlySDK.setEventLogger(abSmartlyEventLogger)

		const contextParams = { units: { deviceId: window.deviceId } }

		abSmartlyContext = window.abSmartlyPrefetchedContext
			? absmartlySDK.createContextWith(contextParams, window.abSmartlyPrefetchedContext)
			: absmartlySDK.createContext(contextParams)

		setAbSmartlyAttributes()

		abSmartlyContext.ready().then(abSmartlyReady).catch(abSmartlyError)
	}

	function setAbSmartlyAppAreaAttribute(appArea) {
		if (appArea) {
			abSmartlyContext.attributes({
				app_area: appArea,
			})
		}
	}

	// Watch networkResourceIsReady to init ABSmartly
	const unwatchNetwork = watch(networkResourceIsReady, (value) => {
		if (value) {
			initAbSmartly()
		}
	})

	// Track exposure add/remove via $onAction — fires once per call, matching old Vuex
	// $store.subscribe behavior. A watch() on the getter would batch multiple exposures
	// added in the same tick and only send the last one.
	const unsubscribeExposures = mainStore.$onAction(({ name, args, after }) => {
		if (name === 'addAbSmartlyExposure') {
			after(() => {
				if (!abSmartlyContext) return
				setAbSmartlyAnalyticsUserProperty()
				sendAbSmartlyExposureToAnalyticsAndBraze(args[0])
			})
		} else if (name === 'removeAbSmartlyExposure') {
			after(() => {
				if (!abSmartlyContext) return
				setAbSmartlyAnalyticsUserProperty()
			})
		}
	})

	// Watch user changes
	const unwatchUser = watch(user, (newVal) => {
		if (!abSmartlyContext || !newVal) return
		setAbSmartlyUserAttributes(newVal)
	})

	// Watch subscription changes
	const unsubscribeSubscription = subscriptionStore.$subscribe(() => {
		if (!abSmartlyContext) return
		setAbSmartlySubscriptionAttributes()
	})

	// Watch geo changes
	const unwatchGeo = watch(countryCode, (newVal) => {
		if (!abSmartlyContext || !newVal) return
		setAbSmartlyCountryAttributes(newVal, regionCode.value)
	})

	// Watch appAreaConfig changes
	const unwatchAppArea = watch(appAreaConfig, (newVal) => {
		if (!abSmartlyContext || !newVal) return
		setAbSmartlyAppAreaAttribute(newVal.appArea)
	})

	onBeforeUnmount(() => {
		unwatchNetwork()
		unsubscribeExposures()
		unwatchUser()
		unsubscribeSubscription()
		unwatchGeo()
		unwatchAppArea()
	})
}
